import React from 'react';
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, ErrorOutline as ErrorIcon, ViewAgenda as SplitIcon } from '@mui/icons-material';
import { PreprocessState } from '../../services/knowledgeApi';

export const isProcessing = (s?: PreprocessState | null) => s?.status === 'queued' || s?.status === 'running';

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function aiDetails(s: PreprocessState): string {
  const parts: string[] = [];
  if (s.units != null) parts.push(`${plural(s.units, 'snippet')}`);
  if (s.pages) parts.push(`from ${plural(s.pages, 'page')}`);
  if (s.credits != null) parts.push(`${s.credits.toFixed(s.credits < 10 ? 2 : 0)} credits`);
  return parts.join(' · ');
}

interface Props {
  state?: PreprocessState | null;
  vectorized: boolean;
  onClick: () => void;
}

/** How a Knowledge Hub file was turned into snippets: AI-organized, standard split, or in progress. */
export const PreprocessChip: React.FC<Props> = ({ state, vectorized, onClick }) => {
  const s = state || {};
  let label: string;
  let tip: string;
  let color: 'default' | 'secondary' | 'warning' | 'error' | 'success' = 'default';
  let icon: React.ReactElement | undefined;
  let variant: 'outlined' | 'filled' = 'outlined';

  if (isProcessing(s)) {
    const p = s.progress;
    label = s.requested ? `AI organizing${p && p.total ? ` ${p.done}/${p.total}` : '…'}` : 'Processing…';
    tip = s.requested
      ? 'The AI is rewriting this file into self-contained snippets. Large files take a few minutes; the old snippets stay searchable until it finishes.'
      : 'Reading and splitting this file.';
    icon = <CircularProgress size={12} />;
    color = s.requested ? 'secondary' : 'default';
  } else if (s.status === 'failed') {
    label = 'Processing failed';
    tip = `${s.reason || 'Something went wrong.'} Click to try again.`;
    color = 'error';
    icon = <ErrorIcon fontSize="small" />;
  } else if (s.method === 'ai') {
    const partial = s.ai === 'partial';
    label = partial ? 'AI-organized (partly)' : 'AI-organized';
    tip = `Rewritten by AI into self-contained, titled snippets with page references. ${aiDetails(s)}.${
      partial && s.reason ? ` ${s.reason}` : ''
    }${s.notes ? ` Your notes: “${s.notes}”` : ''} Click to re-run, change notes or switch back.`;
    color = partial ? 'warning' : 'secondary';
    variant = 'filled';
    icon = <AutoAwesomeIcon fontSize="small" />;
  } else if (s.requested && (s.ai === 'skipped' || s.ai === 'failed')) {
    label = 'Standard split';
    tip = `AI organizing wasn't used: ${s.reason || 'unknown reason'} Split by headings, pages and rows instead. Click for options.`;
    color = 'warning';
    icon = <SplitIcon fontSize="small" />;
  } else if (vectorized) {
    label = 'Standard split';
    tip = s.method
      ? 'Split by headings, pages and rows, without AI. Click to have AI organize it into self-contained snippets.'
      : 'Split before smarter splitting existed. Click to re-process it (standard or AI-organized).';
    icon = <SplitIcon fontSize="small" />;
  } else if (s.requested) {
    label = 'AI organize on';
    tip = 'Will be AI-organized when you turn on vectorizing.';
    icon = <AutoAwesomeIcon fontSize="small" />;
  } else {
    return null;
  }

  return (
    <Tooltip title={tip}>
      <Chip size="small" label={label} color={color} variant={variant} icon={icon} onClick={onClick} sx={{ mt: 0.5 }} />
    </Tooltip>
  );
};
